import React, {useEffect} from 'react';
import {appCtx, useFetchData} from '../app/AppContext';
import {commafyPlural, getPct, MS_MIN} from '../tools/CommonJS';
import {UIProgressBar} from '../tools/UIStackedBar';
import {AnimateDivHeight} from '../tools/UIAnimateToggleDiv';
import {icn} from '../tools/UIIcons';
import {useRerender} from '../tools/CommonReact';
import {HA_topics} from '../app/MisconData';
import {ROUTES} from '../routePaths.js';
const dismissedReg = {};

export function ClientNotice({
	api, params, ktag, cond, condTemp, progress, title, msg, alt,
	onClick = () => appCtx.nav.to(ROUTES.HEALTH, {}, {isReset: true})
}) {
	const rerender = useRerender();
	const {rsp} = useFetchData({api, params, noErrMsg: true, noAutoFetch: dismissedReg[ktag], pollMs: 60 * MS_MIN, alt});
	const show = !dismissedReg[ktag] && rsp && (!condTemp || condTemp(rsp)) && (!cond || cond(rsp));

	useEffect(() => {
		if (!rsp || show || (condTemp && !condTemp(rsp)) || dismissedReg[ktag]) return;
		dismissedReg[ktag] = true;
		rerender();                     // if not showing, possibly rerender to reset polling.
	}, [rsp, show]);

	return <AnimateDivHeight className='bg-bg4/80 rounded-lg'>
		{show &&
			<div className={`w-full bg-sec/10`}>
				<div className='flex items-center py-2 pl-8 pr-4 gap-4'>
					<div className='w-full cursor-pointer' onClick={onClick}>
						<span className='text-sec text-base'>{title}</span>&nbsp;
						<span className='text-h2'>{msg(rsp)}</span>
					</div>
					<div className='cursor-pointer' onClick={() => (dismissedReg[ktag] = true, rerender())}>{icn.remove35}<div/>
					</div>
				</div>
				{progress && <UIProgressBar {...rsp && progress(rsp)} classNameBar='bg-sec/60 rounded-full' className='bg-bg3 border-none h-2 rounded-full'/>}
			</div>}
	</AnimateDivHeight>;
}

export const ClientNoticeEOL = () => (
	<ClientNotice
		ktag='client-notice-eol'
		api='/api/v2/windows-agent-cert-end-of-life'
		cond={rsp => rsp.unsupportedDevices * 100 / rsp.totalDevices > 15}
		progress={rsp => ({val: rsp.unsupportedDevices, total: rsp.totalDevices})}
		title='Sensor update required:'
		msg={rsp =>
			`${getPct(rsp.unsupportedDevices, rsp.totalDevices, 0)} of your Windows devices are running a Sensor version prior to v2.39, which ${
				new Date() < new Date('09/01/2025')? 'will': 'has'
			} become obsolete on Sep-1-2025. Please ensure you have the proper Upgrade Batches in place.`}
		alt={{totalDevices: 10000, unsupportedDevices: 1000}}/>
);

export const ClientNoticePolicy = ({policyId}) => (
	<ClientNotice
		ktag={`client-notice-policy-${policyId}`}
		api='/api/v2/policy_unsupported-policy'
		params={{policyId}}
		cond={rsp => rsp.totalDevices > 0 && rsp.supportedDevices * 100 / rsp.totalDevices < 85}
		progress={rsp => ({val: rsp.totalDevices - rsp.supportedDevices, total: rsp.totalDevices})}
		title='Limited policy support:'
		msg={rsp => `${getPct(rsp.totalDevices - rsp.supportedDevices, rsp.totalDevices, 0)} of relevant assigned devices do not support this policy, please upgrade your Sensors.`}
		alt={{supportedDevices: 4400, totalDevices: 10000}}/>
);

export const ClientNoticeCIS = ({isOn}) => (
	<ClientNotice
		ktag='client-notice-cis'
		api='/api/v2/windows-agent-cert-end-of-life'
		cond={rsp => isOn && rsp.cisUnsupportedDevices * 100 / rsp.totalDevices > 15}
		condTemp={() => isOn}
		progress={rsp => ({val: rsp.cisUnsupportedDevices, total: rsp.totalDevices})}
		title='Sensor update required:'
		msg={rsp =>
			`${getPct(rsp.cisUnsupportedDevices, rsp.totalDevices, 0)} of your devices are running a Sensor version below v3.0.0, lacking support Custom CIS. Please update your sensors to the latest version.`}
		alt={{totalDevices: 10000, cisUnsupportedDevices: 1000}}/>
);

const PHP2SHELL_TOPIC = 'gytPhpSessionUploadProgress';
const php2ShellTopic = rsp => rsp.topics?.find(({topicCode}) => topicCode === PHP2SHELL_TOPIC);

export const ClientNoticePhp2Shell = () => (
	<ClientNotice
		ktag='client-notice-php2shell'
		api='/api/v2/miscon_group-topics'
		cond={rsp => php2ShellTopic(rsp)?.alertsBad > 0}
		title='PHP2Shell detected:'
		msg={rsp => {
			const {alertsBad, devicesBad} = php2ShellTopic(rsp);
			return `${commafyPlural(alertsBad, 'alert')} on ${commafyPlural(devicesBad, 'device')} for the PHP2Shell CVSS 9.2 vulnerability. Review and remediate affected devices.`;
		}}
		onClick={() => appCtx.nav.to(ROUTES.MISCON_OVERVIEW_TOPIC, {topicCode: PHP2SHELL_TOPIC, tObj: [HA_topics[PHP2SHELL_TOPIC]?.title ?? 'PHP2Shell']})}       // Fallback until analyzer-types ships the topic.
		alt={{topics: [{topicCode: PHP2SHELL_TOPIC, alertsBad: 12, devicesBad: 8}]}}/>
);
